import React, { FC } from 'react';
import useIsSurveyTemplatesPage from '@hooks/useIsSurveyTemplatesPage';
import {
  DEFAULT_PAGE_SIZE,
  SURVEY_TABLE_SKELETON_DEFAULT_PAGE_SIZE,
} from '@src/utils/constants';

interface TableSkeletonProps {
  columns?: number;
  height?: string;
  rows?: number;
  dataTestId?: string;
  noHorizontalPadding?: boolean;
  showPagination?: boolean;
  columnWidths?: number[];
}

const TableSkeleton: FC<TableSkeletonProps> = ({
  columns = 8,
  height,
  rows,
  dataTestId,
  noHorizontalPadding = false,
  showPagination = false,
  columnWidths,
}) => {
  const isSurveyTemplate = useIsSurveyTemplatesPage();
  const rowCount = rows
    ? rows
    : isSurveyTemplate
      ? SURVEY_TABLE_SKELETON_DEFAULT_PAGE_SIZE
      : DEFAULT_PAGE_SIZE;

  const cellStyle = (index: number) =>
    columnWidths?.[index] ? { flex: columnWidths[index] } : undefined;

  const minHeight = height
    ? height
    : isSurveyTemplate
      ? 'calc(100vh - 166px)'
      : undefined;

  return (
    <div
      className="table-skeleton-container"
      style={minHeight ? { minHeight } : undefined}
      data-test-id={dataTestId}
    >
      <div
        className={`table-skeleton${noHorizontalPadding ? ' table-skeleton--no-horizontal-padding' : ''}${showPagination ? ' table-skeleton--paginated' : ''}`}
      >
        <div className="table-head">
          {Array.from({ length: columns }).map((_, index) => (
            <div key={index} className="table-cell" style={cellStyle(index)}>
              <div className="skeleton skeleton-text" />
            </div>
          ))}
        </div>

        <div className="table-body">
          {Array.from({ length: rowCount }).map((_, rowIndex) => (
            <div key={rowIndex} className="table-row">
              {Array.from({ length: columns }).map((_, colIndex) => (
                <div
                  key={colIndex}
                  className="table-cell"
                  style={cellStyle(colIndex)}
                >
                  <div className="skeleton skeleton-rect" />
                </div>
              ))}
            </div>
          ))}
        </div>

        {showPagination && (
          <div className="table-skeleton-pagination">
            <div className="pagination-entries">
              <div className="skeleton skeleton-select" />
              <div className="skeleton skeleton-entries" />
              <div className="skeleton skeleton-refresh" />
            </div>
            <div className="pagination-pages">
              <div className="skeleton skeleton-nav" />
              <div className="skeleton skeleton-page" />
              <div className="skeleton skeleton-page" />
              <div className="skeleton skeleton-page" />
              <div className="skeleton skeleton-nav" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TableSkeleton;
